import {
  APIReferenceAttributes,
  ArdoqId,
  Entity,
  UnpersistedEntity,
  ProvenanceSource,
} from '@ardoq/api-types';
import { actionCreator } from '@ardoq/rxbeach';
import { ArdoqError, CidArgs } from '@ardoq/common-helpers';
import { NewReferenceAttributes } from 'scopeData/scopeEditUtils/types';

export type AddReferencesPayload = {
  references: APIReferenceAttributes[];
};

export const addReferences = actionCreator<AddReferencesPayload>(
  '[ReferenceActions] ADD_REFERENCES'
);

export const toggleReferenceDescription = actionCreator<CidArgs>(
  '[ReferenceActions] TOGGLE_REFERENCE_DESCRIPTION'
);

export const selectReference = actionCreator<CidArgs>(
  '[ReferenceActions] SELECT_REFERENCE'
);

export const deleteReference = actionCreator<ArdoqId>(
  '[ReferenceActions] REFERENCE_DELETE'
);

export const reverseReference = actionCreator<ArdoqId>(
  '[ReferenceActions] REFERENCE_REVERSE'
);

export const reverseReferences = actionCreator<ArdoqId[]>(
  '[ReferenceActions] REFERENCES_REVERSE'
);

type LockReferencesPayload = {
  referenceIds: ArdoqId[];
  userId: ArdoqId;
};

export const lockReferences = actionCreator<LockReferencesPayload>(
  '[ReferenceActions] LOCK_REFERENCES'
);

export const unlockReferences = actionCreator<ArdoqId[]>(
  '[ReferenceActions] UNLOCK_REFERENCES'
);

export const notifyReferenceLocked = actionCreator<ArdoqId>(
  '[ComponentActions] NOTIFY_REFERENCE_LOCKED'
);

export const notifyReferenceUnlocked = actionCreator<ArdoqId>(
  '[ComponentActions] NOTIFY_REFERENCE_UNLOCKED'
);

export const resetReferences = actionCreator(
  '[ReferenceActions] RESET_REFERENCES'
);

export const resetReferencesWithData = actionCreator<APIReferenceAttributes[]>(
  '[ReferenceActions] RESET_REFERENCES_WITH_DATA'
);

export type RemoveReferencesPayload = {
  ids: ArdoqId[];
};

export const removeReferences = actionCreator<RemoveReferencesPayload>(
  '[ReferenceActions] REMOVE_REFERENCES'
);

export const updateReference = actionCreator<
  Partial<APIReferenceAttributes> & Entity
>('[ReferenceActions] UPDATE_REFERENCE');

export const batchLoadReferences = actionCreator<APIReferenceAttributes[]>(
  '[ReferenceActions] BATCH_LOAD_REFERENCES'
);

export const batchRemoveReferences = actionCreator<ArdoqId[]>(
  '[ReferenceActions] BATCH_REMOVE_REFERENCES'
);

export type CreateReferencePayload = {
  attributes: UnpersistedEntity<APIReferenceAttributes>;
  provenanceSource?: ProvenanceSource;
};

export const createReference = actionCreator<CreateReferencePayload>(
  '[ReferenceActions] CREATE_REFERENCE'
);

export type CreateReferencesPayload = {
  referenceAttributes: Omit<NewReferenceAttributes, 'targetWorkspace'>[];
  isScenarioMode: boolean;
};

export const createReferences = actionCreator<CreateReferencesPayload>(
  '[ReferenceActions] CREATE_REFERENCES'
);

export const createReferencesSuccess = actionCreator<APIReferenceAttributes[]>(
  '[ReferenceActions] CREATE_REFERENCES_SUCCESS'
);

export const createReferencesFailed = actionCreator<ArdoqError>(
  '[ReferenceActions] CREATE_REFERENCES_FAILED'
);

export type SaveReferencesPayload = {
  attributes: APIReferenceAttributes[];
  isScenarioMode: boolean;
  provenanceSource?: ProvenanceSource;
};

export const saveReferencesAction = actionCreator<SaveReferencesPayload>(
  '[ReferenceActions] SAVE_REFERENCES'
);

export const saveReferencesSuccess = actionCreator<APIReferenceAttributes[]>(
  '[ReferenceActions] SAVE_REFERENCES_SUCCESS'
);

export const saveReferencesFailed = actionCreator<ArdoqError>(
  '[ReferenceActions] SAVE_REFERENCES_FAILED'
);

export const flipReference = actionCreator<APIReferenceAttributes>(
  '[ReferenceActions] FLIP_REFERENCE'
);

export const setScenarioRelatedReferenceIds = actionCreator<ArdoqId[]>(
  '[ReferenceActions] SET_SCENARIO_RELATED_REFERENCE_IDS'
);

export type DeleteReferencesPayload = {
  referenceIds: ArdoqId[];
  isScenarioMode: boolean;
  provenanceSource?: ProvenanceSource;
};

export const deleteReferences = actionCreator<DeleteReferencesPayload>(
  '[ReferenceActions] DELETE_REFERENCES'
);

export const deleteReferencesSuccess = actionCreator<ArdoqId[]>(
  '[ReferenceActions] DELETE_REFERENCES_SUCCESS'
);

export const deleteReferencesFailed = actionCreator<ArdoqError>(
  '[ReferenceActions] DELETE_REFERENCES_FAILED'
);
